import { ArrowDownLeft, ArrowUpRight, ExternalLink } from 'lucide-react';
import {
  getAddressBlockExplorerUrl,
  getNetworkDisplayName,
  getTransactionBlockExplorerUrl,
  type IndexedTokenMovement,
  type NetworkType,
} from '@mls-wallet/shared-types';
import { CopyAddressButton } from '@/components/shared/copy-address-button';
import { TokenLogo } from '@/components/shared/token-logo';
import { StatusBadge } from '@/components/shared/status-badge';
import { Button } from '@/components/ui/button';
import { formatAddress } from '@/lib/utils';
import type { AddressBookExactEntry } from '@/types/address-book';
import { StyledTableCell, StyledTableRow } from '@/components/ui/styled-table';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { formatTransactionDate } from './utils';

function MovementAmount({ movement }: { movement: IndexedTokenMovement }) {
  const amount = movement.token.quantity + ' ' + movement.token.symbol;
  const received = movement.direction === 'received';
  const DirectionIcon = received ? ArrowDownLeft : ArrowUpRight;
  return (
    <div className="flex min-w-0 items-start gap-2">
      <div className="mt-0.5 shrink-0">
        <DirectionIcon
          className={
            received ? 'h-4 w-4 text-emerald-600' : 'h-4 w-4 text-rose-400'
          }
          aria-hidden
        />
      </div>
      <div className="min-w-0 space-y-1">
        <p
          className="flex min-w-0 items-center gap-1 whitespace-nowrap text-base font-semibold leading-tight"
          title={amount}
        >
          <span className="min-w-0 truncate">
            {received ? 'Received' : 'Sent'} {movement.token.quantity}
          </span>
          <TokenLogo
            symbol={movement.token.symbol}
            logoUrl={movement.token.logoUrl ?? undefined}
            size="sm"
            className="shrink-0"
          />
          <span className="shrink-0">{movement.token.symbol}</span>
        </p>
        <p className="truncate text-xs font-normal text-muted-foreground">
          {movement.description || movement.token.name}
        </p>
      </div>
    </div>
  );
}

function TransferParty({
  address,
  name,
  networkName,
}: {
  address: string;
  name?: string | undefined;
  networkName: string;
}) {
  return (
    <div className="min-w-0 space-y-1">
      <p
        className={
          name ? 'truncate text-sm font-medium' : 'truncate font-mono text-sm'
        }
        title={address}
      >
        {name || formatAddress(address)}
      </p>
      <p className="flex min-w-0 items-center gap-1 text-xs font-normal text-muted-foreground">
        <span className="shrink-0">{networkName}</span>
        {name && (
          <>
            <span aria-hidden>·</span>
            <span className="truncate font-mono">{formatAddress(address)}</span>
          </>
        )}
      </p>
    </div>
  );
}

function TransferAddress({
  address,
  name,
  networkId,
}: {
  address: string;
  name?: string | undefined;
  networkId: NetworkType;
}) {
  const explorerUrl = getAddressBlockExplorerUrl(address, networkId);
  return (
    <div className="min-w-0 space-y-1.5">
      {name && <p className="font-medium text-foreground">{name}</p>}
      <div className="flex items-start gap-2">
        <span className="min-w-0 flex-1 break-all font-mono text-xs leading-5 text-foreground/80">
          {address}
        </span>
        <div className="flex shrink-0 items-center">
          <CopyAddressButton address={address} />
          {explorerUrl && (
            <Button
              variant="ghost"
              size="icon"
              asChild
              className="size-5 text-muted-foreground"
            >
              <a
                href={explorerUrl}
                target="_blank"
                rel="noreferrer"
                aria-label="View address on block explorer"
              >
                <ExternalLink className="size-3.5" />
              </a>
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export function IndexedTokenMovementRow({
  movement,
  addressBookEntryByAddress,
  onOpen,
}: {
  movement: IndexedTokenMovement;
  addressBookEntryByAddress: ReadonlyMap<string, AddressBookExactEntry>;
  onOpen: () => void;
}) {
  const networkId = movement.networkId;
  const networkName = getNetworkDisplayName(networkId);
  const received = movement.direction === 'received';
  const fromName =
    addressBookEntryByAddress.get(movement.fromAddress.toLowerCase())?.name ||
    (!received ? movement.account.name : undefined);
  const toName =
    addressBookEntryByAddress.get(movement.toAddress.toLowerCase())?.name ||
    (received ? movement.account.name : undefined);
  return (
    <StyledTableRow
      tabIndex={0}
      aria-label={
        (received ? 'Received ' : 'Sent ') +
        movement.token.quantity +
        ' ' +
        movement.token.symbol +
        (received ? ' in ' : ' from ') +
        movement.account.name
      }
      onClick={onOpen}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpen();
        }
      }}
      className="cursor-pointer focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring [&>td]:py-3.5"
    >
      <StyledTableCell>
        <MovementAmount movement={movement} />
      </StyledTableCell>
      <StyledTableCell>
        <TransferParty
          address={movement.fromAddress}
          name={fromName}
          networkName={networkName}
        />
      </StyledTableCell>
      <StyledTableCell>
        <TransferParty
          address={movement.toAddress}
          name={toName}
          networkName={networkName}
        />
      </StyledTableCell>
      <StyledTableCell className="pr-6 text-sm text-muted-foreground">
        —
      </StyledTableCell>
      <StyledTableCell className="whitespace-nowrap text-sm font-medium text-foreground/80">
        {formatTransactionDate(movement.executedAt)}
      </StyledTableCell>
      <StyledTableCell>
        <StatusBadge
          status={movement.direction}
          className="whitespace-nowrap px-2.5 py-0.5 text-[12px] font-medium"
        />
      </StyledTableCell>
      <StyledTableCell />
    </StyledTableRow>
  );
}

/** Indexed transfers have read-only details and no proposal actions. */
export function IndexedTokenMovementSheet({
  movement,
  addressBookEntryByAddress,
  onClose,
}: {
  movement: IndexedTokenMovement | null;
  addressBookEntryByAddress: ReadonlyMap<string, AddressBookExactEntry>;
  onClose: () => void;
}) {
  const networkId = movement?.networkId ?? null;
  const received = movement?.direction !== 'sent';
  const fromName = movement
    ? addressBookEntryByAddress.get(movement.fromAddress.toLowerCase())?.name ||
      (!received ? movement.account.name : undefined)
    : undefined;
  const toName = movement
    ? addressBookEntryByAddress.get(movement.toAddress.toLowerCase())?.name ||
      (received ? movement.account.name : undefined)
    : undefined;
  const DirectionIcon = received ? ArrowDownLeft : ArrowUpRight;
  const explorerUrl =
    movement && networkId
      ? getTransactionBlockExplorerUrl(movement.transactionHash, networkId)
      : null;
  return (
    <Sheet
      open={movement !== null}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <SheetContent className="flex w-full flex-col gap-0 p-0 sm:max-w-xl">
        <SheetHeader className="shrink-0 space-y-2 border-b px-6 py-6 text-left">
          <SheetTitle>
            {received ? 'Received tokens' : 'Sent tokens'}
          </SheetTitle>
          <SheetDescription>
            A completed token transfer {received ? 'into' : 'out of'} your
            account.
          </SheetDescription>
          <div className="flex items-center gap-2 pt-1">
            <StatusBadge status={received ? 'received' : 'sent'} />
            {networkId && (
              <span className="text-xs text-muted-foreground">
                {getNetworkDisplayName(networkId)}
              </span>
            )}
          </div>
        </SheetHeader>
        {movement && networkId && (
          <div className="min-h-0 flex-1 space-y-5 overflow-y-auto bg-muted/40 p-6">
            <div className="rounded-xl border bg-background p-5">
              <p className="text-xs text-muted-foreground">
                Amount {received ? 'received' : 'sent'}
              </p>
              <div className="mt-3 flex items-center gap-3">
                {movement.token.logoUrl ? (
                  <TokenLogo
                    symbol={movement.token.symbol}
                    logoUrl={movement.token.logoUrl}
                    size="xl"
                    className="shrink-0"
                  />
                ) : (
                  <div
                    className={
                      received
                        ? 'flex size-10 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-950/40 dark:text-emerald-400'
                        : 'flex size-10 shrink-0 items-center justify-center rounded-full bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400'
                    }
                  >
                    <DirectionIcon className="size-5" aria-hidden />
                  </div>
                )}
                <div className="min-w-0 space-y-1">
                  <p className="text-xl font-semibold leading-snug tracking-tight tabular-nums [overflow-wrap:anywhere] sm:text-2xl">
                    {movement.token.quantity} {movement.token.symbol}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {movement.token.name}
                  </p>
                </div>
              </div>
            </div>
            <dl className="divide-y rounded-xl border bg-background text-sm">
              <div className="space-y-2 p-5">
                <dt className="text-xs text-muted-foreground">From</dt>
                <dd>
                  <TransferAddress
                    address={movement.fromAddress}
                    name={fromName}
                    networkId={networkId}
                  />
                </dd>
              </div>
              <div className="space-y-2 p-5">
                <dt className="text-xs text-muted-foreground">To</dt>
                <dd>
                  <TransferAddress
                    address={movement.toAddress}
                    name={toName}
                    networkId={networkId}
                  />
                </dd>
              </div>
            </dl>
            {movement.description && (
              <div className="space-y-2 rounded-xl border bg-background p-5">
                <p className="text-xs text-muted-foreground">Description</p>
                <p className="whitespace-pre-wrap break-words text-sm">
                  {movement.description}
                </p>
              </div>
            )}
            <dl className="divide-y rounded-xl border bg-background text-sm">
              <div className="flex items-center justify-between gap-4 px-5 py-4">
                <dt className="text-muted-foreground">Network</dt>
                <dd className="text-right font-medium">
                  {getNetworkDisplayName(networkId)}
                </dd>
              </div>
              <div className="flex items-start justify-between gap-4 px-5 py-4">
                <dt className="text-muted-foreground">Executed (UTC)</dt>
                <dd className="text-right">
                  <p className="font-medium">
                    {new Intl.DateTimeFormat('en-US', {
                      dateStyle: 'medium',
                      timeZone: 'UTC',
                    }).format(new Date(movement.executedAt))}
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    {new Intl.DateTimeFormat('en-US', {
                      timeStyle: 'medium',
                      timeZone: 'UTC',
                      hour12: false,
                    }).format(new Date(movement.executedAt))}{' '}
                    UTC
                  </p>
                </dd>
              </div>
              <div className="space-y-2 p-5">
                <dt className="flex items-center justify-between gap-3 text-xs text-muted-foreground">
                  <span>Transaction hash</span>
                  <CopyAddressButton
                    address={movement.transactionHash}
                    ariaLabel="Copy transaction hash"
                  />
                </dt>
                <dd className="break-all font-mono text-xs leading-5 text-foreground/80">
                  {movement.transactionHash}
                </dd>
              </div>
            </dl>
            {explorerUrl && (
              <Button
                variant="outline"
                asChild
                className="w-full gap-2 bg-background"
              >
                <a href={explorerUrl} target="_blank" rel="noreferrer">
                  View on block explorer <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
